import {
	Button,
	Divider,
	Header,
	Label,
	Message,
	Modal,
	Segment,
} from '@mairistem/composants';
import { getErrorMessage } from '@mairistem/finances-utils';
import React from 'react';
import { toast } from 'react-toastify';
import type { Bureau } from '../../../interfaces/bureaux';
import type Collectivite from '../../../interfaces/collectivite';
import type { UtilisateurCollectivite } from '../../../interfaces/utilisateurCollectivite';
import { getBureaux, updateBureau } from '../../../services/pastellService';
import './UtilisateurBureauModal.css';

interface UtilisateurBureauModalProps {
	selectedUtilisateur: UtilisateurCollectivite;
	onClose: () => void;
	onUpdateBureaux: () => void;
	isLoading?: boolean;
}

const UtilisateurBureauModal = ({
	selectedUtilisateur,
	onUpdateBureaux,
	onClose,
	isLoading,
}: UtilisateurBureauModalProps) => {
	const [bureauxSelectionnes, setBureauxSelectionnes] = React.useState<
		Set<string>
	>(new Set());
	const [isUpdating, setIsUpdating] = React.useState<boolean>(false);
	const [bureaux, setBureaux] = React.useState<Bureau[]>([]);
	const [isBureauxLoading, setIsBureauxLoading] = React.useState<boolean>(true);

	React.useEffect(() => {
		const fetchBureaux = async () => {
			try {
				const response: Collectivite[] = await getBureaux();
				const allBureaux: Bureau[] = [];

				response.forEach((entite: Collectivite) => {
					entite.bureaux?.forEach((bureau: Bureau) => {
						allBureaux.push({
							...bureau,
							id_e: entite.id_e,
							isSelected: false,
						});
					});
				});

				setBureaux(allBureaux);
			} catch (error) {
				toast.error(
					getErrorMessage(error, 'Erreur lors de la récupération des bureaux'),
				);
			} finally {
				setIsBureauxLoading(false);
			}
		};

		fetchBureaux();
	}, []);

	// Initialiser les bureaux sélectionnés avec ceux que l'utilisateur possède déjà
	React.useEffect(() => {
		const initialSelection = new Set<string>();
		selectedUtilisateur.collectivites.forEach((col) => {
			if (col.bureaux) {
				col.bureaux.forEach((bureau) => {
					initialSelection.add(bureau.id.toString());
				});
			}
		});
		setBureauxSelectionnes(initialSelection);
	}, [selectedUtilisateur]);

	const handleToggleBureau = (bureauId: string) => {
		setBureauxSelectionnes((prev) => {
			const newSet = new Set(prev);
			if (newSet.has(bureauId)) {
				newSet.delete(bureauId);
			} else {
				newSet.add(bureauId);
			}
			return newSet;
		});
	};

	const handleValidate = async () => {
		setIsUpdating(true);
		try {
			// Calculer les bureaux ajoutés et supprimés
			const bureauxInitiaux = new Set<string>();
			selectedUtilisateur.collectivites.forEach((col) => {
				if (col.bureaux) {
					col.bureaux.forEach((bureau) => {
						bureauxInitiaux.add(bureau.id.toString());
					});
				}
			});

			const bureauxAjoutes = bureaux.filter(
				(bureau) =>
					bureauxSelectionnes.has(bureau.id) && !bureauxInitiaux.has(bureau.id),
			);

			const bureauxSupprimes = bureaux.filter(
				(bureau) =>
					!bureauxSelectionnes.has(bureau.id) && bureauxInitiaux.has(bureau.id),
			);

			if (bureauxAjoutes.length === 0 && bureauxSupprimes.length === 0) {
				toast.info('Aucune modification à enregistrer');
				onClose();
				return;
			}

			await updateBureau(selectedUtilisateur, bureauxAjoutes, bureauxSupprimes);
			toast.success('Bureaux mis à jour avec succès');
			onUpdateBureaux();
			onClose();
		} catch (error) {
			toast.error(
				getErrorMessage(error, 'Erreur lors de la mise à jour des bureaux'),
			);
		} finally {
			setIsUpdating(false);
		}
	};

	return (
		<Modal onClose={onClose} open>
			<Modal.Header>Gérer les bureaux</Modal.Header>
			<Modal.Content>
				<Modal.Description>
					<Message className="message-replace-user" info>
						<Message.Header>Utilisateur sélectionné :</Message.Header>
						<div data-testid="bureau-modal-user-name">
							<strong>Nom: </strong>
							{selectedUtilisateur?.prenom} {selectedUtilisateur?.nom}
						</div>
						<div data-testid="bureau-modal-user-login">
							<strong>Login: </strong>
							{selectedUtilisateur?.login ?? selectedUtilisateur?.email}
						</div>
					</Message>

					<Segment loading={isBureauxLoading}>
						<Header as="h4">Bureaux disponibles</Header>
						{!isBureauxLoading && bureaux.length === 0 && (
							<Message warning>Aucun bureau disponible</Message>
						)}
						{!isBureauxLoading && bureaux.length > 0 && (
							<>
								<p>
									Cliquez sur les bureaux pour les ajouter ou les supprimer.
								</p>
								<Divider hidden />
								<div className="bureaux-list-segment">
									{bureaux.map((bureau: Bureau) => (
										<Label
											key={bureau.id}
											className="bureau-user"
											circular
											basic
											onClick={() => handleToggleBureau(bureau.id)}
											color={
												bureauxSelectionnes.has(bureau.id) ? 'blue' : undefined
											}
											style={{ cursor: 'pointer' }}
										>
											{bureau.name}
										</Label>
									))}
								</div>
							</>
						)}
					</Segment>
				</Modal.Description>
			</Modal.Content>
			<Modal.Actions>
				<Button color="black" onClick={onClose}>
					Annuler
				</Button>
				<Button
					icon="checkmark"
					onClick={handleValidate}
					primary
					loading={isUpdating || isLoading}
					disabled={isBureauxLoading}
				>
					Valider
				</Button>
			</Modal.Actions>
		</Modal>
	);
};

export default UtilisateurBureauModal;
